import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import CommonTable, {
  IActionData,
  ITableColumn,
} from '@/components/common/CommonTable';
import MainHeading from '@/components/common/MainHeading';
import StatusIcon from '@/components/Icons/StatusIcon';
import Layout from '@/components/Layout/Layout';
import AllocationApprovalModal from '@/components/Modal/AllocationApprovalModal';
import AssetRequestDetailModal from '@/components/Modal/AssetRequestDetailModal';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import {
  IAssetRequestApiResponse,
  IAssetRequestUpdate,
  IAssetRequest,
  IAssetCategoryResponse,
  IAssetCategory,
  IAssetTableData,
} from '@/interfaces/assets.model';
import { IDeleteResponse, IFilterSideBarForm } from '@/interfaces/common.model';
import { RootState } from '@/redux/store';
import {
  showToastNotification,
  getFilterUrl,
  orderByList,
  assetRequestStatus,
} from '@/utility/helper';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import NavTabs from '@/components/common/NavTabs';
import TabContainer from '@/components/common/TabContainer';
import moment from 'moment';
import ViewIcon from '@/components/Icons/ViewIcon';
import FilterComponent from '@/components/common/FilterComponent';
import useFilter from '@/hooks/useFilter';
import { useDispatch } from 'react-redux';
import { setUpdateFilterStates } from '@/redux/features/UserSlice';

interface IPageState {
  currentPage: number;
  count: number;
  sortByDate: string[];
  totalPages: number;
  tableLoader: boolean;
  requests: IAssetTableData[];
  filterData: string;
  searchQuery: string;
  assetCategory: IAssetCategory[];
}

const AssetRequests = () => {
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const dispatch = useDispatch();
  const [requestId, setRequestId] = useState<string | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apporvalModal, setApprovalModal] = useState(false);
  const [assetCategoryId, setAssetCategoryId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<IAssetTableData | null>(null);
  const [activeTab, setActiveTab] = useState(1);

  const tabs = [
    {
      tabId: 1,
      label: 'Allocate',
      activeClass: 'btn px-5 rounded-start-2 rounded-end-0 active nav-tab-link',
      inactiveClass: 'btn px-5 rounded-start-2 rounded-end-0 nav-tab-link',
    },
    {
      tabId: 2,
      label: 'Transfer',
      activeClass: 'btn px-5 rounded-start-0 rounded-end-0 active nav-tab-link',
      inactiveClass: 'btn px-5 rounded-start-0 rounded-end-0 nav-tab-link',
    },
    {
      tabId: 3,
      label: 'Return',
      activeClass: 'btn px-5 rounded-start-0 rounded-end-2 active nav-tab-link',
      inactiveClass: 'btn px-5 rounded-start-0 rounded-end-2 nav-tab-link',
    },
  ];

  const toggleTab = (tab: number) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setPageStates((prev) => ({ ...prev, currentPage: 1 }));
    }
  };
  const [pageStates, setPageStates] = useState<IPageState>({
    currentPage: 1,
    count: 0,
    totalPages: 0,
    tableLoader: false,
    requests: [],
    sortByDate: [
      moment().startOf('month').format('YYYY-MM-DD'),
      moment().endOf('month').format('YYYY-MM-DD'),
    ],
    filterData: '',
    assetCategory: [],
    searchQuery: '',
  });
  const tableHeading: ITableColumn<IAssetTableData>[] = [
    { value: 'index', label: '#' },
    { value: 'asset', label: 'Asset' },
    { value: 'assetCategory', label: 'Asset Category' },
    { value: 'requestedBy', label: 'Requested By' },
    { value: 'requestedOn', label: 'Requested On' },
    // { value: 'requirements', label: 'Requirements' },
    { value: 'assetRequestStatus', label: 'Status' },
  ];
  const ActionData: IActionData<IAssetTableData>[] = [
    {
      label: 'View',
      className: 'btn btn-link ',
      icon: <ViewIcon />,
      title: 'view',
      clickHandler: (data: IAssetTableData) => {
        setSelectedRequest(data);
        setIsDetailModalOpen(true);
      },
    },
    {
      label: 'Approve',
      className: 'btn btn-link text-success',
      icon: <StatusIcon status="approved" />,
      title: 'Approve',
      clickHandler: (data: IAssetTableData) => {
        if (data.isReturn) {
          handleApproveReturn(data.id);
        } else {
          setAssetCategoryId(data.assetCategoryId);
          setRequestId(data.id);
          setApprovalModal(true);
        }
      },
      disabled: (data: IAssetTableData) =>
        Number(data.assetRequestStatus) !== 0,
      isHidden: (data: IAssetTableData) =>
        Number(data.assetRequestStatus) !== 0,
    },
    {
      label: 'Reject',
      className: 'btn btn-link text-danger',
      icon: <StatusIcon status="rejected" />,
      title: 'Reject',
      clickHandler: (data: IAssetTableData) => {
        setIsConfirmModalOpen(true);
        setRequestId(data.id);
      },
      disabled: (data: IAssetTableData) =>
        Number(data.assetRequestStatus) !== 0,
      isHidden: (data: IAssetTableData) =>
        Number(data.assetRequestStatus) !== 0,
    },
  ];

  useEffect(() => {
    getAssetRequestList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageStates?.sortByDate,
    filterDetails?.assetCategoryId,
    filterDetails?.assetStatus,
    filterDetails?.pageSize,
    pageStates?.currentPage,
    pageStates?.searchQuery,
    filterDetails?.orderBy,
    activeTab,
  ]);
  useEffect(() => {
    getCategories();
  }, []);

  const getCategories = async () => {
    try {
      const response: IAssetCategoryResponse = await callApi(
        `${API_URL}${API_ENDPOINT?.getAssetsCategory}`,
        {
          method: 'GET',
        },
      );
      if (response.status) {
        setPageStates((prev) => ({
          ...prev,
          assetCategory: response.data?.assetCategory,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getAssetRequestList = async () => {
    try {
      setPageStates((prev) => ({ ...prev, tableLoader: true }));
      const filter = {
        search: pageStates.searchQuery,
        pageSize: filterDetails?.pageSize,
        pageNumber: pageStates.currentPage,
        orderBy: filterDetails?.orderBy,
        startDate: pageStates?.sortByDate[0],
        endDate: pageStates?.sortByDate[1],
        assetCategoryId: filterDetails?.assetCategoryId,
        // status: filterDetails?.assetStatus,
        ...(activeTab === 1 && { isReturn: false, isTransfer: false }),
        ...(activeTab === 2 && { isTransfer: true }),
        ...(activeTab === 3 && { isReturn: true }),
      };

      const response: IAssetRequestApiResponse = await callApi(
        API_URL + API_ENDPOINT.assetRequestList + '?' + getFilterUrl(filter),
        { method: 'GET' },
      );
      console?.log('response', response);
      if (response?.status) {
        const responseData = response?.data?.['asset Request'] || [];
        const newData: IAssetTableData[] = responseData?.map(
          (item: IAssetRequest, index) => ({
            id: item.id ?? '',
            assetId: item.assetId ?? '',
            createdAt: item.createdAt ?? '',
            assetCategory: item.assetCategory?.name ?? '--',
            assetCategoryId: item.assetCategory?.id ?? '',
            asset: item.asset?.name ?? 'Not Assigned',
            requirements: item.description ?? '-',
            description: item.description ?? '-',
            requestedOn: moment(item.createdAt).format('DD-MM-YYYY') ?? '-',
            assetRequestStatus: Number(item.status),
            requestedBy: item?.assignedUser?.name || '-',
            index: index + 1,
            isReturn: Boolean(item.isReturn),
            isTransfer: Boolean(item.isTransfer),
          }),
        );
        setPageStates((pre) => ({
          ...pre,
          requests: newData,
          count: response?.data?.pageInfo?.total ?? 0,
          totalPages: response?.data?.pageInfo?.totalPages ?? 0,
          tableLoader: false,
        }));
      } else {
        setPageStates((prev) => ({ ...prev, tableLoader: false }));
      }
    } catch (error) {
      setPageStates((prev) => ({ ...prev, tableLoader: false }));
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((prev) => ({ ...prev, tableLoader: false }));
    }
  };

  const handleApproveReturn = async (reqId: string) => {
    const data: IAssetRequestUpdate = {
      status: 1,
      // isReturn: true,
    };
    try {
      setIsLoading(true);
      const response: IDeleteResponse = await callApi(
        `${API_URL + API_ENDPOINT.updateAssetRequest}?assetRequestId=${reqId}`,
        { method: 'POST', data: data },
      );
      if (response?.status) {
        showToastNotification(
          'success',
          'Asset Return Request Approved successfully',
        );
        getAssetRequestList();
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (assetId: string) => {
    const data: IAssetRequestUpdate = {
      status: 1,
      isAllocate: true,
      assetId: assetId,
    };
    try {
      setIsLoading(true);
      const response: IDeleteResponse = await callApi(
        `${API_URL + API_ENDPOINT.updateAssetRequest}?assetRequestId=${requestId}`,
        { method: 'POST', data: data },
      );
      if (response?.status) {
        showToastNotification('success', 'Asset Request Approved successfully');
        getAssetRequestList();
        setRequestId(null);
        setApprovalModal(false);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async () => {
    const data = {
      status: 2,
    };
    try {
      setIsLoading(true);
      const response: IDeleteResponse = await callApi(
        `${API_URL + API_ENDPOINT.updateAssetRequest}?assetRequestId=${requestId}`,
        { method: 'POST', data: data },
      );
      if (response?.status) {
        showToastNotification('success', 'Asset Request Rejected successfully');
        getAssetRequestList();
        setIsConfirmModalOpen(false);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };
  const sortHandler = (data: Partial<IFilterSideBarForm> | null) => {
    if (data === null) {
      dispatch(
        setUpdateFilterStates({
          assetCategoryId: '',
          assetStatus: '',
        }),
      );
      return;
    }
    if (data) {
      dispatch(
        setUpdateFilterStates({
          assetCategoryId: data.assetCategoryId,
          assetStatus: data?.assetStatus,
        }),
      );
    }
    setPageStates((prev) => ({ ...prev, currentPage: 1 }));
  };
  const dateChangeHandler = (Dates: Date[] | null) => {
    let formattedDate = null;
    if (Dates && Dates.length === 2) {
      const startDate = new Date(
        Dates[0].getTime() - Dates[0].getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 10);
      const endDate = new Date(
        Dates[1].getTime() - Dates[1].getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 10);
      formattedDate = [startDate, endDate];
    }
    if (formattedDate !== null) {
      setPageStates((prevState) => ({
        ...prevState,
        sortByDate: formattedDate,
      }));
    } else {
      setPageStates((prevState) => ({ ...prevState, sortByDate: [] }));
    }
  };

  const { handleSearchChange, orderByHandler, handleChangePageSize } =
    useFilter(pageStates, setPageStates);
  return (
    <>
      <Layout>
        <div className="content">
          <MainHeading
            title={'Asset Requests'}
            dateChangeHandler={dateChangeHandler}
            showDefaultDate={true}
          />
          <NavTabs tabs={tabs} onTabClick={toggleTab} activeTab={activeTab} />
          <TabContainer activeTab={activeTab} tabId={activeTab}>
            <FilterComponent
              handleSearch={handleSearchChange}
              orderByList={orderByList}
              isGrid={false}
              sortHandler={sortHandler}
              filterArray={{ isShow: true }}
              orderByHandler={orderByHandler}
              assetSortList={{
                assetCategory: pageStates?.assetCategory,
                assetStatus: assetRequestStatus,
              }}
            />
            <CommonTable<IAssetTableData>
              tableHeading={tableHeading}
              tableData={pageStates?.requests}
              ActionData={ActionData}
              count={pageStates?.count}
              pageSize={filterDetails?.pageSize}
              isLoading={pageStates?.tableLoader}
              currentPage={pageStates?.currentPage}
              totalPages={pageStates?.totalPages}
              pageChangeHandler={(page) =>
                setPageStates((prev) => ({ ...prev, currentPage: page }))
              }
              handleChangePageSize={handleChangePageSize}
            />
          </TabContainer>
        </div>
      </Layout>
      <AllocationApprovalModal
        isOpen={apporvalModal}
        assetCategoryId={assetCategoryId}
        isLoading={isLoading}
        submitHandler={handleApprove}
        toggle={() => setApprovalModal(false)}
      />
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        isLoading={isLoading}
        submitHandler={handleReject}
        toggle={() => setIsConfirmModalOpen(false)}
        confirmReason="Reject"
        hideReason={true}
      />
      <AssetRequestDetailModal
        isOpen={isDetailModalOpen}
        toggle={() => setIsDetailModalOpen(false)}
        data={selectedRequest}
      />
    </>
  );
};

export default AssetRequests;
